import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanMatch,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '@environments/environment';
import { AuthService } from '@app/core/auth/auth';
import { SessionStorageService } from '@app/core/auth/session-storage';
import { WindowRef } from '@app/core/providers/window-ref';

@Injectable({ providedIn: 'root' })
export class AuthGuardService implements CanActivate, CanMatch {
  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly sessionService: SessionStorageService,
    private readonly windowRef: WindowRef,
  ) {}

  public async canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    let urlParams = new URLSearchParams(this.windowRef.search);
    const targetPathSplit = _state.url.split('?');
    const targetPath = targetPathSplit[0];
    // パス付きでログインする場合は「_state.url」に認証情報が入る
    if (targetPathSplit.length > 1) {
      urlParams = new URLSearchParams(targetPathSplit[1]);
    }

    if (this.isLogin(urlParams)) {
      const idToken = urlParams.get('idToken')!;
      const verified = this.authService.verifyIdToken(idToken);
      if (!verified) return this.redirectToLogin();

      const decodedIdToken = this.authService.decodeIdToken(idToken);
      if (!decodedIdToken) return this.redirectToLogin();

      this.sessionService.saveAuthToken(
        urlParams.get('applicationClientId')!,
        urlParams.get('accessToken')!,
        idToken,
        urlParams.get('refreshToken')!,
        decodedIdToken.hospitalCode,
        decodedIdToken['custom:x-uid'],
        decodedIdToken.name ?? '',
      );

      return this.adminGuardDecision(targetPath);
    }

    if (this.sessionService.hasAuthToken()) {
      const idToken = this.sessionService.getIdToken()!;
      const verified = this.authService.verifyIdToken(idToken);
      if (verified) return this.adminGuardDecision(targetPath);

      try {
        await firstValueFrom(this.authService.tokenRefresh());

        const newIdToken = this.sessionService.getIdToken()!;
        const reverification = this.authService.verifyIdToken(newIdToken);
        if (!reverification) return this.redirectToLogin();

        return this.adminGuardDecision(targetPath);
      } catch {
        return this.redirectToLogin();
      }
    }

    return this.redirectToLogin();
  }

  public canMatch(route: Route, _segments: UrlSegment[]): boolean | UrlTree {
    if (route.path !== '**') return true;

    const qIdToken = new URLSearchParams(this.windowRef.search).get('idToken');
    const idToken = qIdToken ?? this.sessionService.getIdToken();
    if (!idToken) return this.redirectToLogin();

    const target = this.authService.isAdminUser(idToken) ? 'admin' : 'hospital';
    return this.router.createUrlTree(['/', target]);
  }

  private adminGuardDecision(targetPath: string): true | UrlTree {
    if (!(targetPath === '/admin' || targetPath.startsWith('/admin/')))
      return true;
    return this.authService.isAdminUser()
      ? true
      : this.router.createUrlTree(['/', 'hospital']);
  }

  private isLogin(urlParams: URLSearchParams): boolean {
    const applicationClientId = urlParams.get('applicationClientId');
    const accessToken = urlParams.get('accessToken');
    const idToken = urlParams.get('idToken');
    const refreshToken = urlParams.get('refreshToken');
    return !!(applicationClientId && accessToken && idToken && refreshToken);
  }

  private redirectToLogin(): false {
    this.sessionService.logout();
    this.windowRef.href = `${environment.cgwAuthWebUrl}?applicationCode=${environment.applicationName}#redirectUrl=${this.windowRef.href}`;
    return false;
  }
}
